import { styled } from "goober";
import { type RefObject, useRef, useState } from "react";
import { isValidStepTestName } from "../../shared/stepTests/constants";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { ModalContent, ModalFooter } from "./ModalCard";
import { Text } from "./Text";
import { fieldOutline } from "./fieldOutline";

const FieldGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Field = styled("label")`
  display: flex;
  align-items: stretch;
  background-color: var(--surface-primary);
  ${fieldOutline("&:focus-within")}
  border-radius: 8px;
  height: 32px;
  cursor: text;
  overflow: hidden;
`;

const NameInput = styled("input")`
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  background: transparent;
  border: 0;
  outline: 0;
  color: var(--text-primary);
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;

  &::placeholder {
    opacity: 0.3;
  }
`;

const Suffix = styled("span")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 12px;
  background-color: var(--surface-secondary);
  border-left: 0.5px solid var(--border-alpha);
  color: var(--text-secondary);
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  white-space: nowrap;
`;

type Props = {
  open: boolean;
  title: string;
  confirmLabel: string;
  initialName: string;
  // Names that would collide. Callers exclude the current name so an unchanged
  // rename stays valid (and no-ops).
  takenNames: readonly string[];
  onClose: () => void;
  onSubmit: (name: string) => void;
};

export function StepTestNameModal({ open, onClose, ...rest }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Modal open={open} onClose={onClose} alert initialFocusRef={inputRef}>
      {open && <Body onClose={onClose} inputRef={inputRef} {...rest} />}
    </Modal>
  );
}

function Body({
  title,
  confirmLabel,
  initialName,
  takenNames,
  onClose,
  onSubmit,
  inputRef,
}: Omit<Props, "open"> & { inputRef: RefObject<HTMLInputElement | null> }) {
  const [value, setValue] = useState(initialName);

  const trimmed = value.trim();
  const validChars = isValidStepTestName(trimmed);
  const taken = validChars && takenNames.includes(trimmed);
  const valid = validChars && !taken;
  const helper = taken
    ? { text: "That name is already taken.", error: true }
    : trimmed.length > 0 && !validChars
      ? {
          text: "Use letters, numbers, dashes, and underscores — no spaces.",
          error: true,
        }
      : {
          text: "Use letters, numbers, dashes, and underscores.",
          error: false,
        };

  const submit = () => {
    if (!valid) return;
    onSubmit(trimmed);
    onClose();
  };

  return (
    <ModalContent style={{ width: 320 }}>
      <Text variant="text-regular-md" color="var(--text-primary)">
        {title}
      </Text>
      <FieldGroup>
        <Field>
          <NameInput
            ref={inputRef}
            value={value}
            placeholder="Step test name"
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submit();
              }
            }}
          />
          <Suffix>.output</Suffix>
        </Field>
        <Text
          variant="text-small"
          color={helper.error ? "var(--red-600)" : "var(--text-secondary)"}
        >
          {helper.text}
        </Text>
      </FieldGroup>
      <ModalFooter>
        <Button size="l" variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button size="l" variant="primary" onClick={submit} disabled={!valid}>
          {confirmLabel}
        </Button>
      </ModalFooter>
    </ModalContent>
  );
}
